import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useGetAccessUsersQuery } from "../api/Endpoints/Core/Security/Users";
import { getCurrentUser, hasAccess } from "../helpers/session_helper";

// ─── Per-system menu slices ───────────────────────────────────────────────────
import { assetGuardMenuItems }        from "./Components/AssetGuardMenuItems";
import { fleetGuardMenuItems }        from "./Components/FleetGuardMenuItems";
import { coreMenuItems }              from "./Components/CoreMenuItems";
import { dmsMenuItems }               from "./Components/DMSMenuItems";
import { buildFmsMenuItems }          from "./Components/FMSMenuItems";
import { payflowMenuItems }           from "./Components/PayflowMenuItems";
import { buildPeopleSyncMenuItems }   from "./Components/PeopleSyncMenuItems";   // ← factory (was static)
import { buildProcurementMenuItems }  from "./Components/ProcumentMenuItems";
import { buildNationalTeamMenuItems } from "./Components/NTMSMenuItems";

const Navdata = () => {
  const history  = useNavigate();
  const location = useLocation();

  const [iscurrentState, setIscurrentState] = useState("Dashboard");

  const shouldFetch = getCurrentUser();
  const { data, refetch } = useGetAccessUsersQuery({}, { skip: !shouldFetch });
  const accessRights = data?.returnData?.map(({ permissionTypeId, allowRead, allowWrite, allowDelete }) => ({
    permissionTypeId, allowRead, allowWrite, allowDelete,
  }));

  useEffect(() => { refetch(); }, [location.pathname]);
  useEffect(() => {
    if (accessRights) sessionStorage.setItem("accessRights", JSON.stringify(accessRights));
  }, [accessRights]);

  // ── Top-level collapse states ───────────────────────────────────────────
  const [isApps,        setIsApps]        = useState(false);
  const [isAuth,        setIsAuth]        = useState(false);
  const [isPages,       setIsPages]       = useState(false);
  const [isDashboard,   setIsDashboard]   = useState(false);
  const [isAssetGuard,  setIsAssetGuard]  = useState(false);
  const [isFleetGuard,  setIsFleetGuard]  = useState(false);
  const [isFMS,         setIsFMS]         = useState(false);
  const [isPeopleSync,  setIsPeopleSync]  = useState(false);
  const [isPayflow,     setIsPayflow]     = useState(false);
  const [isDMS,         setIsDMS]         = useState(false);
  const [isProcurement, setIsProcurement] = useState(false);
  const [isNationalTeam,setIsNationalTeam]= useState(false);

  // ── FMS sub-collapse ────────────────────────────────────────────────────
  const [isFMSAccounting,   setIsFMSAccounting]   = useState(false);
  const [isFMSBudget,       setIsFMSBudget]       = useState(false);
  const [isFMSCollection,   setIsFMSCollection]   = useState(false);
  const [isFMSDisbursement, setIsFMSDisbursement] = useState(false);

  // ── PeopleSync sub-collapse ─────────────────────────────────────────────
  const [isPeopleSyncWorkforce,   setIsPeopleSyncWorkforce]   = useState(false);
  const [isPeopleSyncTimekeeping, setIsPeopleSyncTimekeeping] = useState(false);
  const [isPeopleSyncSelfService, setIsPeopleSyncSelfService] = useState(false);

  // ── Procurement sub-collapse ────────────────────────────────────────────
  const [isProcPreProcurement,    setIsProcPreProcurement]    = useState(false);
  const [isProcBacManagement,     setIsProcBacManagement]     = useState(false);
  const [isProcAdvertisement,     setIsProcAdvertisement]     = useState(false);
  const [isProcBidEvaluation,     setIsProcBidEvaluation]     = useState(false);
  const [isProcPostQualification, setIsProcPostQualification] = useState(false);
  const [isProcAwardContract,     setIsProcAwardContract]     = useState(false);
  const [isProcImplementation,    setIsProcImplementation]    = useState(false);

  // ── National Team sub-collapse ──────────────────────────────────────────
  const [isNtAthleteProfile, setIsNtAthleteProfile] = useState(false);
  const [isNtPayroll,        setIsNtPayroll]        = useState(false);
  const [isNtMedical,        setIsNtMedical]        = useState(false);
  const [isNtNutrition,      setIsNtNutrition]      = useState(false);
  const [isNtDormitory,      setIsNtDormitory]      = useState(false);

  // ── Auto-collapse on section change ────────────────────────────────────
  useEffect(() => {
    document.body.classList.remove("twocolumn-panel");

    if (iscurrentState !== "Apps")       setIsApps(false);
    if (iscurrentState !== "Auth")       setIsAuth(false);
    if (iscurrentState !== "Pages")      setIsPages(false);
    if (iscurrentState !== "Dashboard")  setIsDashboard(false);
    if (iscurrentState !== "AssetGuard") setIsAssetGuard(false);
    if (iscurrentState !== "FleetGuard") setIsFleetGuard(false);
    if (iscurrentState !== "Payflow")    setIsPayflow(false);
    if (iscurrentState !== "DMS")        setIsDMS(false);

    // FMS — collapse all sub-groups when leaving the FMS section
    if (!iscurrentState.startsWith("FMS")) {
      setIsFMS(false);
      setIsFMSAccounting(false);
      setIsFMSBudget(false);
      setIsFMSCollection(false);
      setIsFMSDisbursement(false);
    }

    // PeopleSync — collapse the outer accordion AND all inner sub-groups
    // when the user navigates away from any PeopleSync page
    if (!iscurrentState.startsWith("PeopleSync")) {
      setIsPeopleSync(false);
      setIsPeopleSyncWorkforce(false);
      setIsPeopleSyncTimekeeping(false);
      setIsPeopleSyncSelfService(false);
    }

    // Procurement — collapse all sub-groups when leaving the Procurement section
    if (!iscurrentState.startsWith("Proc")) {
      setIsProcurement(false);
      setIsProcPreProcurement(false);
      setIsProcBacManagement(false);
      setIsProcAdvertisement(false);
      setIsProcBidEvaluation(false);
      setIsProcPostQualification(false);
      setIsProcAwardContract(false);
      setIsProcImplementation(false);
    }

    // National Team — collapse all sub-groups when leaving the NT section
    if (!iscurrentState.startsWith("Nt")) {
      setIsNationalTeam(false);
      setIsNtAthleteProfile(false);
      setIsNtPayroll(false);
      setIsNtMedical(false);
      setIsNtNutrition(false);
      setIsNtDormitory(false);
    }
  }, [history, iscurrentState]);

  // ── Build FMS items ─────────────────────────────────────────────────────
  const fmsMenuItems = buildFmsMenuItems({
    isFMSAccounting,   setIsFMSAccounting,
    isFMSBudget,       setIsFMSBudget,
    isFMSCollection,   setIsFMSCollection,
    isFMSDisbursement, setIsFMSDisbursement,
    setIscurrentState,
  });

  // ── Build PeopleSync items ──────────────────────────────────────────────
  const peopleSyncMenuItems = buildPeopleSyncMenuItems({
    isPeopleSyncWorkforce,   setIsPeopleSyncWorkforce,
    isPeopleSyncTimekeeping, setIsPeopleSyncTimekeeping,
    isPeopleSyncSelfService, setIsPeopleSyncSelfService,
    setIscurrentState,
  });

  // ── Build Procurement items ─────────────────────────────────────────────
  const procurementMenuItems = buildProcurementMenuItems({
    isProcPreProcurement,    setIsProcPreProcurement,
    isProcBacManagement,     setIsProcBacManagement,
    isProcAdvertisement,     setIsProcAdvertisement,
    isProcBidEvaluation,     setIsProcBidEvaluation,
    isProcPostQualification, setIsProcPostQualification,
    isProcAwardContract,     setIsProcAwardContract,
    isProcImplementation,    setIsProcImplementation,
    setIscurrentState,
  });

  // ── Build National Team items ───────────────────────────────────────────
  const nationalTeamMenuItems = buildNationalTeamMenuItems({
    isNtAthleteProfile, setIsNtAthleteProfile,
    isNtPayroll,        setIsNtPayroll,
    isNtMedical,        setIsNtMedical,
    isNtNutrition,      setIsNtNutrition,
    isNtDormitory,      setIsNtDormitory,
    setIscurrentState,
  });

  // ── withToggle — wires outer accordion state onto top-level menu items ──
  function withToggle(items, stateVar, setter, stateKey) {
    return items.map(function (item) {
      if (!item.subItems) return item;
      return Object.assign({}, item, {
        stateVariables: stateVar,
        click: function (e) {
          e.preventDefault();
          setter(!stateVar);
          setIscurrentState(stateKey);
        },
      });
    });
  }

  // ── Assemble full menu ──────────────────────────────────────────────────
  const SYSTEMS_HEADER_IDX = 1;
  const beforeSystems = coreMenuItems.slice(0, SYSTEMS_HEADER_IDX + 1);
  const afterSystems  = coreMenuItems.slice(SYSTEMS_HEADER_IDX + 1);

  const menuItems = [
    ...beforeSystems,
    ...withToggle(nationalTeamMenuItems,  isNationalTeam, setIsNationalTeam, "NtRoot"),
    ...withToggle(assetGuardMenuItems,    isAssetGuard,   setIsAssetGuard,   "AssetGuard"),
    ...withToggle(fleetGuardMenuItems,    isFleetGuard,   setIsFleetGuard,   "FleetGuard"),
    ...withToggle(fmsMenuItems,           isFMS,          setIsFMS,          "FMS"),
    ...withToggle(peopleSyncMenuItems,    isPeopleSync,   setIsPeopleSync,   "PeopleSync"),
    ...withToggle(payflowMenuItems,       isPayflow,      setIsPayflow,      "Payflow"),
    ...withToggle(dmsMenuItems,           isDMS,          setIsDMS,          "DMS"),
    ...withToggle(procurementMenuItems,   isProcurement,  setIsProcurement,  "Procurement"),
    ...afterSystems,
  ];

  // ── Permission filter ───────────────────────────────────────────────────
  const filterMenu = (items) => {
    return items
      .filter(item => hasAccess(item.permissionTypeId, accessRights))
      .map(item => {
        const newItem = { ...item };
        if (item.subItems) {
          newItem.subItems = filterMenu(item.subItems);
          if (newItem.subItems.length === 0) return null;
        }
        if (item.childItems) {
          newItem.childItems = filterMenu(item.childItems);
          if (newItem.childItems.length === 0) return null;
        }
        return newItem;
      })
      .filter(Boolean);
  };

  return <React.Fragment>{filterMenu(menuItems)}</React.Fragment>;
};

export default Navdata;